import { Clock, Users, Download, RefreshCw, Zap, Database, Trash2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { useAdmin } from './AdminContext';

export const AdminOverviewTab = () => {
  const { stats, siteStats, recentActivity, initializeDatabase, syncVenueEvents, cleanupTestData } = useAdmin();

  return (
    <>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 sm:gap-4 mb-4 sm:mb-6">
        <Card className="bg-card border-border">
          <CardHeader className="pb-2 px-3 sm:px-6">
            <CardDescription className="text-xs sm:text-sm">Artists</CardDescription>
            <CardTitle className="text-xl sm:text-3xl">{stats?.artists || 0}</CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            <p className="text-[10px] sm:text-xs text-muted-foreground">{stats?.artists_active || 0} active, {stats?.artists_banned || 0} banned</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardHeader className="pb-2 px-3 sm:px-6">
            <CardDescription className="text-xs sm:text-sm">Food Trucks</CardDescription>
            <CardTitle className="text-xl sm:text-3xl">{stats?.food_trucks || 0}</CardTitle>
          </CardHeader>
          <CardContent className="px-3 sm:px-6">
            <p className="text-[10px] sm:text-xs text-muted-foreground">{stats?.food_trucks_active || 0} active</p>
          </CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardHeader className="pb-2"><CardDescription>Vendors</CardDescription><CardTitle className="text-3xl">{stats?.vendors || 0}</CardTitle></CardHeader>
          <CardContent><p className="text-xs text-muted-foreground">{stats?.vendors_active || 0} active</p></CardContent>
        </Card>
        <Card className="bg-card border-border">
          <CardHeader className="pb-2"><CardDescription>Events</CardDescription><CardTitle className="text-3xl">{stats?.events || 0}</CardTitle></CardHeader>
        </Card>
      </div>

      {/* Pending Items */}
      <Card className="bg-yellow-500/10 border-yellow-500/30 mb-6">
        <CardHeader><CardTitle className="text-yellow-500 flex items-center gap-2"><Clock size={20} /> Pending Approvals</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Bands', value: stats?.band_registrations_pending },
            { label: 'Venues', value: stats?.venue_registrations_pending },
            { label: 'Promoters', value: stats?.promoter_registrations_pending },
            { label: 'Events', value: stats?.event_submissions_pending },
            { label: 'Features', value: stats?.feature_requests_pending },
            { label: 'Messages', value: stats?.contact_messages_new, color: 'text-blue-500' },
          ].map(({ label, value, color }) => (
            <div key={label} className="text-center">
              <p className={`text-2xl font-bold ${color || 'text-yellow-500'}`}>{value || 0}</p>
              <p className="text-sm text-muted-foreground">{label}</p>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* User Stats */}
      <Card className="bg-purple-500/10 border-purple-500/30 mb-6">
        <CardHeader><CardTitle className="text-purple-400 flex items-center gap-2"><Users size={20} /> User Statistics</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-4 gap-4">
          {[
            { label: 'Total Users', value: stats?.users_total, color: 'text-purple-400' },
            { label: 'Regular Users', value: stats?.users_regular, color: 'text-green-400' },
            { label: 'Artists', value: stats?.users_artists, color: 'text-blue-400' },
            { label: 'Admins', value: stats?.users_admins, color: 'text-yellow-400' },
          ].map(({ label, value, color }) => (
            <div key={label} className="text-center">
              <p className={`text-2xl font-bold ${color}`}>{value || 0}</p>
              <p className="text-sm text-muted-foreground">{label}</p>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* App Install Statistics */}
      <Card className="bg-green-500/10 border-green-500/30 mb-6">
        <CardHeader><CardTitle className="text-green-400 flex items-center gap-2"><Download size={20} /> App Installs</CardTitle></CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
            {[
              { label: 'Total Installs', value: siteStats?.installs?.total, color: 'text-green-400', bg: 'bg-green-500/10' },
              { label: 'Today', value: siteStats?.installs?.today, color: 'text-blue-400', bg: 'bg-background/50' },
              { label: 'This Week', value: siteStats?.installs?.this_week, color: 'text-purple-400', bg: 'bg-background/50' },
              { label: 'This Month', value: siteStats?.installs?.this_month, color: 'text-orange-400', bg: 'bg-background/50' },
            ].map(({ label, value, color, bg }) => (
              <div key={label} className={`text-center p-4 ${bg} rounded-lg`}>
                <p className={`text-${label === 'Total Installs' ? '3' : '2'}xl font-bold ${color}`}>{value || 0}</p>
                <p className="text-sm text-muted-foreground">{label}</p>
              </div>
            ))}
          </div>
          {siteStats?.installs?.by_platform && Object.keys(siteStats.installs.by_platform).length > 0 && (
            <div className="mt-4 pt-4 border-t border-border">
              <p className="text-sm text-muted-foreground mb-3">Installs by Platform:</p>
              <div className="flex flex-wrap gap-2">
                {Object.entries(siteStats.installs.by_platform).map(([platform, count]) => (
                  <span key={platform} className={`px-3 py-1 rounded-full text-sm ${platform === 'ios' ? 'bg-blue-500/20 text-blue-400' : platform === 'android' ? 'bg-green-500/20 text-green-400' : platform === 'desktop' ? 'bg-purple-500/20 text-purple-400' : 'bg-gray-500/20 text-gray-400'}`}>
                    {platform === 'ios' ? 'iOS' : platform === 'android' ? 'Android' : platform === 'desktop' ? 'Desktop' : platform}: {count}
                  </span>
                ))}
              </div>
            </div>
          )}
          {siteStats?.recent_installs && siteStats.recent_installs.length > 0 && (
            <div className="mt-4 pt-4 border-t border-border">
              <p className="text-sm text-muted-foreground mb-3">Recent Installs:</p>
              <div className="space-y-2 max-h-40 overflow-y-auto">
                {siteStats.recent_installs.slice(0, 5).map((install, idx) => (
                  <div key={idx} className="flex items-center justify-between text-sm p-2 bg-background/30 rounded">
                    <span className={`px-2 py-0.5 rounded text-xs ${install.platform === 'ios' ? 'bg-blue-500/20 text-blue-400' : install.platform === 'android' ? 'bg-green-500/20 text-green-400' : 'bg-purple-500/20 text-purple-400'}`}>
                      {install.platform === 'ios' ? 'iOS' : install.platform === 'android' ? 'Android' : install.platform}
                    </span>
                    <span className="text-muted-foreground text-xs">{new Date(install.created_at).toLocaleString()}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Recent Activity */}
      <Card className="bg-card border-border">
        <CardHeader><CardTitle className="flex items-center gap-2"><RefreshCw size={20} /> Recent Activity</CardTitle></CardHeader>
        <CardContent>
          {recentActivity.length === 0 ? (
            <p className="text-muted-foreground text-center py-4">No recent activity</p>
          ) : (
            <div className="space-y-3">
              {recentActivity.slice(0, 10).map((item, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-background/50 rounded-lg">
                  <div className="flex items-center gap-3">
                    <div className={`w-2 h-2 rounded-full ${item.status === 'pending' ? 'bg-yellow-500' : item.status === 'new' ? 'bg-blue-500' : item.status === 'approved' || item.status === 'published' ? 'bg-green-500' : 'bg-gray-500'}`} />
                    <div>
                      <p className="text-sm font-medium">{item.title}</p>
                      <p className="text-xs text-muted-foreground capitalize">{item.type.replace('_', ' ')}</p>
                    </div>
                  </div>
                  <span className={`text-xs px-2 py-1 rounded-full ${item.status === 'pending' ? 'bg-yellow-500/20 text-yellow-500' : item.status === 'new' ? 'bg-blue-500/20 text-blue-500' : item.status === 'approved' || item.status === 'published' || item.status === 'active' ? 'bg-green-500/20 text-green-500' : 'bg-gray-500/20 text-gray-500'}`}>
                    {item.status}
                  </span>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quick Actions */}
      <Card className="bg-card border-border mb-6">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base"><Zap size={18} className="text-purple-400" /> Quick Actions</CardTitle>
          <CardDescription className="text-xs">Database & sync tools for managing your site</CardDescription>
        </CardHeader>
        <CardContent className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <Button onClick={initializeDatabase} variant="outline" className="w-full bg-blue-500/10 border-blue-500/40 text-blue-400 hover:bg-blue-500/20 h-auto py-3 flex-col gap-1" data-testid="quick-init-db">
            <Database size={18} /><span className="font-bold text-sm">Initialize Database</span><span className="text-[10px] text-muted-foreground">Seed all content & ads</span>
          </Button>
          <Button onClick={syncVenueEvents} variant="outline" className="w-full bg-green-500/10 border-green-500/40 text-green-400 hover:bg-green-500/20 h-auto py-3 flex-col gap-1" data-testid="quick-sync-events">
            <RefreshCw size={18} /><span className="font-bold text-sm">Sync Venue Events</span><span className="text-[10px] text-muted-foreground">Pull latest from venues</span>
          </Button>
          <Button onClick={cleanupTestData} variant="outline" className="w-full bg-red-500/10 border-red-500/40 text-red-400 hover:bg-red-500/20 h-auto py-3 flex-col gap-1" data-testid="quick-cleanup">
            <Trash2 size={18} /><span className="font-bold text-sm">Cleanup Test Data</span><span className="text-[10px] text-muted-foreground">Remove non-canonical data</span>
          </Button>
        </CardContent>
      </Card>
    </>
  );
};
